import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  AlertDialog,
  Button,
  Description,
  Label,
  Modal,
  Spinner,
  Switch,
  toast,
} from '@heroui/react';
import { CheckboxButtonGroup } from '@heroui-pro/react';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useEnums } from '@hs/shared';
import { trpc } from '@/lib/trpc';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import { SkuAppAddModal } from './SkuAppAddModal';

interface SkuAppModalProps {
  open: boolean;
  skuId?: string | null;
  skuName?: string;
  onCancel: () => void;
}

type SkuAppRow = {
  id: string;
  skuId?: string | null;
  appId?: string | null;
  status?: string | null;
  app?: {
    id?: string;
    code?: string | null;
    name?: string | null;
    description?: string | null;
    type?: string | null;
  } | null;
};

export function SkuAppModal({ open, skuId, skuName, onCancel }: SkuAppModalProps) {
  const utils = trpc.useUtils();
  const { getLabel } = useEnums({ trpc: trpc as any, modules: ['app'] });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data: items = [], isLoading } = trpc.bizProduct.skuApp.listAll.useQuery(
    { skuId: skuId ?? undefined, sortBy: 'status', sortOrder: 'asc' },
    { enabled: open && Boolean(skuId) }
  );

  useEffect(() => {
    if (!open) {
      setSelectedIds([]);
      setAddOpen(false);
      setDeleteOpen(false);
    }
  }, [open]);

  const rows = items as SkuAppRow[];

  const existingAppIds = useMemo(
    () =>
      rows
        .map((row) => String(row.appId ?? row.app?.id ?? ''))
        .filter(Boolean),
    [rows]
  );

  const appGroups = useMemo(() => {
    const order = ['SYSTEM_MANAGEMENT', 'SUBSCRIPTION_MANAGEMENT', 'WORKSPACE'];
    const map = new Map<string, SkuAppRow[]>();
    for (const row of rows) {
      const type = row.app?.type || 'UNKNOWN';
      const list = map.get(type) ?? [];
      list.push(row);
      map.set(type, list);
    }
    const keys = [
      ...order.filter((k) => map.has(k)),
      ...[...map.keys()].filter((k) => !order.includes(k)).sort(),
    ];
    return keys.map((key) => ({ key, items: map.get(key) ?? [] }));
  }, [rows]);

  const deleteManyMutation = trpc.bizProduct.skuApp.deleteMany.useMutation({
    onSuccess: (result) => {
      toast.success(`已删除 ${result.count} 个应用关联`);
      setSelectedIds([]);
      setDeleteOpen(false);
      void utils.bizProduct.skuApp.listAll.invalidate();
    },
    onError: (error) => {
      toast.danger('删除失败', { description: error.message });
    },
  });

  const updateMutation = trpc.bizProduct.skuApp.update.useMutation({
    onSuccess: () => {
      void utils.bizProduct.skuApp.listAll.invalidate();
    },
    onError: (error) => {
      toast.danger('状态更新失败', { description: error.message });
    },
  });

  const handleToggleStatus = async (row: SkuAppRow, nextActive: boolean) => {
    if (!row.id) return;
    await updateMutation.mutateAsync({
      id: row.id,
      data: { status: nextActive ? 'ACTIVE' : 'INACTIVE' },
    });
  };

  const handleDelete = () => {
    if (selectedIds.length === 0) {
      toast.warning('请先勾选要删除的应用');
      return;
    }
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    await deleteManyMutation.mutateAsync({ ids: selectedIds });
  };

  return (
    <>
      <Modal isOpen={open} onOpenChange={(next) => !next && onCancel()}>
        <RacDialogTriggerAnchor />
        <Modal.Backdrop>
          <Modal.Container placement="center" scroll="inside">
            <Modal.Dialog className="min-h-[50vh] min-w-[50vw] max-h-[90vh] w-full max-w-[90vw]">
              <Modal.CloseTrigger />
              <Modal.Header className="gap-1">
                <Modal.Heading>关联应用</Modal.Heading>
                <Description className="text-sm">SKU：{skuName || '-'}</Description>
              </Modal.Header>
              <Modal.Body className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Button
                    size="sm"
                    isDisabled={!skuId}
                    onPress={() => setAddOpen(true)}
                  >
                    <IconPlus className="size-4" stroke={1.5} />
                    添加应用
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    isDisabled={!skuId || selectedIds.length === 0}
                    onPress={handleDelete}
                  >
                    <IconTrash className="size-4" stroke={1.5} />
                    删除应用
                  </Button>
                </div>

                {isLoading ? (
                  <div className="flex justify-center py-10">
                    <Spinner />
                  </div>
                ) : rows.length === 0 ? (
                  <p className="py-10 text-center text-sm text-muted">尚未关联任何应用</p>
                ) : (
                  <div className="min-h-0 overflow-y-auto p-2">
                    <CheckboxButtonGroup
                      className="grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
                      layout="grid"
                      name="sku-apps-manage-select"
                      value={selectedIds}
                      onChange={setSelectedIds}
                      variant="secondary"
                    >
                      {appGroups.map((group) => {
                        const groupLabel =
                          getLabel('app', 'type', group.key) || group.key;
                        return (
                          <Fragment key={group.key}>
                            <Label className="col-span-full text-base font-semibold">
                              {groupLabel}
                            </Label>
                            {group.items.map((row) => {
                              const rowId = String(row.id);
                              const isActive = row.status === 'ACTIVE';
                              return (
                                <CheckboxButtonGroup.Item key={rowId} value={rowId}>
                                  <CheckboxButtonGroup.Indicator />
                                  <CheckboxButtonGroup.ItemContent>
                                    <div className="flex min-h-[88px] flex-col gap-2">
                                      <div className="flex flex-col gap-1 pr-6">
                                        <Label>{row.app?.name || '-'}</Label>
                                        {row.app?.description ? (
                                          <Description className="line-clamp-2 text-xs">
                                            {row.app.description}
                                          </Description>
                                        ) : null}
                                      </div>
                                      <div
                                        className="mt-auto flex items-center justify-end gap-2"
                                        onClick={(e) => e.stopPropagation()}
                                        onPointerDown={(e) => e.stopPropagation()}
                                      >
                                        <span className="text-xs text-muted">
                                          {isActive ? '有效' : '停用'}
                                        </span>
                                        <Switch
                                          size="sm"
                                          isSelected={isActive}
                                          isDisabled={updateMutation.isPending}
                                          onChange={(selected) => {
                                            void handleToggleStatus(row, selected);
                                          }}
                                        >
                                          <Switch.Content>
                                            <Switch.Control>
                                              <Switch.Thumb />
                                            </Switch.Control>
                                          </Switch.Content>
                                        </Switch>
                                      </div>
                                    </div>
                                  </CheckboxButtonGroup.ItemContent>
                                </CheckboxButtonGroup.Item>
                              );
                            })}
                          </Fragment>
                        );
                      })}
                    </CheckboxButtonGroup>
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onPress={onCancel}>
                  关闭
                </Button>
              </Modal.Footer>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>

      {skuId ? (
        <SkuAppAddModal
          open={addOpen}
          skuId={skuId}
          skuName={skuName}
          existingAppIds={existingAppIds}
          onCancel={() => setAddOpen(false)}
          onSuccess={() => setAddOpen(false)}
        />
      ) : null}

      <AlertDialog isOpen={deleteOpen} onOpenChange={setDeleteOpen}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除选中的 {selectedIds.length} 个应用关联吗？
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button variant="secondary" onPress={() => setDeleteOpen(false)}>
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deleteManyMutation.isPending}
                  onPress={() => void confirmDelete()}
                >
                  {deleteManyMutation.isPending ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </>
  );
}
